<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import MarkdownContent from './MarkdownContent.vue'
import ViewerIcon from './ViewerIcon.vue'

const FEEDBACK_CATEGORIES = [
  {
    value: 'content_error',
    label: '内容错误',
    placeholder: '选填：请指出错误的内容，也可以填写你了解的正确信息。'
  },
  {
    value: 'recognition_error',
    label: '识别错误',
    placeholder: '选填：请说明被识别错的地点、建筑或物体，以及它的正确名称。'
  },
  {
    value: 'incomplete',
    label: '内容不完整',
    placeholder: '选填：请说明遗漏了哪些重要景观、历史信息或游览建议。'
  },
  {
    value: 'other',
    label: '其他问题',
    placeholder: '必填：请描述你发现的具体问题。'
  }
]

const props = defineProps({
  open: Boolean,
  content: {
    type: String,
    default: ''
  },
  status: {
    type: String,
    default: 'idle'
  },
  error: {
    type: String,
    default: ''
  },
  feedbackStatus: {
    type: String,
    default: 'idle'
  },
  feedbackError: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['close', 'retry', 'submit-feedback', 'reset-feedback'])

const body = ref(null)
const feedbackPanel = ref(null)
const feedbackOpen = ref(false)
const feedbackCategory = ref('content_error')
const feedbackDescription = ref('')
const feedbackValidation = ref('')
const busy = computed(() => ['preparing', 'loading'].includes(props.status))
const generating = computed(() => !props.content && props.status !== 'error')
const feedbackSubmitting = computed(() => props.feedbackStatus === 'submitting')
const feedbackSubmitted = computed(() => props.feedbackStatus === 'success')
const descriptionRequired = computed(() => feedbackCategory.value === 'other')
const feedbackPlaceholder = computed(() => (
  FEEDBACK_CATEGORIES.find(item => item.value === feedbackCategory.value)?.placeholder || ''
))

function scrollFeedbackIntoView() {
  nextTick(() => {
    if (!body.value || !feedbackPanel.value) return
    body.value.scrollTop = Math.max(0, feedbackPanel.value.offsetTop - 8)
  })
}

function openFeedback() {
  feedbackOpen.value = true
  feedbackValidation.value = ''
  scrollFeedbackIntoView()
}

function closeFeedback() {
  if (feedbackSubmitting.value) return
  feedbackOpen.value = false
}

function submitFeedback() {
  const description = feedbackDescription.value.trim()
  if (descriptionRequired.value && !description) {
    feedbackValidation.value = '请填写具体问题'
    return
  }
  feedbackValidation.value = ''
  emit('submit-feedback', {
    category: feedbackCategory.value,
    description
  })
}

function restartFeedback() {
  feedbackCategory.value = 'content_error'
  feedbackDescription.value = ''
  feedbackValidation.value = ''
  emit('reset-feedback')
}

watch(
  () => props.content.length,
  () => {
    if (!busy.value) return
    nextTick(() => {
      if (!body.value || !props.open) return
      const distance = body.value.scrollHeight - body.value.clientHeight - body.value.scrollTop
      if (distance < 160) body.value.scrollTop = body.value.scrollHeight
    })
  }
)

watch(() => props.open, open => {
  if (!open) {
    feedbackOpen.value = false
    return
  }
  nextTick(() => {
    if (body.value) body.value.scrollTop = 0
  })
})

watch(() => props.status, status => {
  if (!['preparing', 'loading'].includes(status)) return
  feedbackOpen.value = false
  feedbackCategory.value = 'content_error'
  feedbackDescription.value = ''
  feedbackValidation.value = ''
})

watch([feedbackCategory, feedbackDescription], () => {
  feedbackValidation.value = ''
})
</script>

<template>
  <section
    class="sheet sheet--ai"
    :class="[`is-${status}`, { 'is-open': open, 'is-generating': generating }]"
    :aria-hidden="!open"
    :aria-labelledby="generating ? undefined : 'ai-introduction-title'"
    :aria-label="generating ? 'AI 导游正在查询资料' : undefined"
  >
    <button class="sheet__handle" type="button" aria-label="收起 AI 导游" @click="$emit('close')">
      <ViewerIcon name="chevron-down" />
    </button>

    <header v-if="!generating" class="ai-introduction__header">
      <div class="ai-introduction__identity">
        <span class="ai-introduction__badge" aria-hidden="true">
          <ViewerIcon name="ai" />
        </span>
        <h2 id="ai-introduction-title">AI 导游</h2>
      </div>
    </header>

    <div ref="body" class="ai-introduction__body" aria-live="polite">
      <div v-if="status === 'error'" class="ai-introduction__error" role="alert">
        <span aria-hidden="true"><ViewerIcon name="ai" /></span>
        <div>
          <strong>AI 导游失败</strong>
          <p>{{ error || '暂时无法完成导游介绍，请稍后重试' }}</p>
        </div>
        <button type="button" @click="$emit('retry')">重试</button>
      </div>

      <article v-else-if="content" class="ai-introduction__article">
        <div class="ai-introduction__content" :class="{ 'is-streaming': busy }">
          <MarkdownContent :content="content" />
        </div>
        <footer v-if="!busy" class="ai-introduction__footer">
          <span>由 MeTop Vision AI 基于当前模型生成 · 仅供参考</span>
          <button type="button" @click="openFeedback">内容有误？反馈</button>
        </footer>

        <section
          v-if="feedbackOpen && !busy"
          ref="feedbackPanel"
          class="ai-guide-feedback"
          aria-labelledby="ai-guide-feedback-title"
        >
          <header>
            <div>
              <strong id="ai-guide-feedback-title">反馈导游内容</strong>
            </div>
            <button type="button" aria-label="关闭错误反馈" :disabled="feedbackSubmitting" @click="closeFeedback">×</button>
          </header>

          <div v-if="feedbackSubmitted" class="ai-guide-feedback__success" role="status">
            <span aria-hidden="true">✓</span>
            <strong>感谢反馈</strong>
            <p>我们已记录这条问题，后续会核查并修正。</p>
            <button type="button" @click="restartFeedback">继续反馈</button>
          </div>

          <form v-else @submit.prevent="submitFeedback">
            <fieldset :disabled="feedbackSubmitting">
              <legend>问题类型</legend>
              <div class="ai-guide-feedback__categories">
                <label v-for="item in FEEDBACK_CATEGORIES" :key="item.value">
                  <input v-model="feedbackCategory" type="radio" name="ai-guide-feedback-category" :value="item.value" />
                  <span>{{ item.label }}</span>
                </label>
              </div>
            </fieldset>

            <label class="ai-guide-feedback__description">
              <span>
                补充说明
                <small>{{ descriptionRequired ? '必填' : '选填' }}</small>
              </span>
              <textarea
                v-model="feedbackDescription"
                maxlength="2000"
                :required="descriptionRequired"
                :disabled="feedbackSubmitting"
                :placeholder="feedbackPlaceholder"
              ></textarea>
              <small>{{ feedbackDescription.length }}/2000</small>
            </label>

            <p v-if="feedbackValidation || feedbackError" class="ai-guide-feedback__error" role="alert">
              {{ feedbackValidation || feedbackError }}
            </p>

            <div class="ai-guide-feedback__actions">
              <button type="button" :disabled="feedbackSubmitting" @click="closeFeedback">取消</button>
              <button class="is-primary" type="submit" :disabled="feedbackSubmitting" :aria-busy="feedbackSubmitting">
                {{ feedbackSubmitting ? '正在提交' : '提交反馈' }}
              </button>
            </div>
          </form>
        </section>
      </article>

      <div v-else class="ai-introduction__generating" role="status" aria-live="polite">
        <strong>AI 导游正在查询资料</strong>
      </div>
    </div>
  </section>
</template>
